/**
 * Where a control that names one or more negotiations should land.
 *
 * MOVED, NOT WRITTEN. This lived as a module-private function in
 * companyDashboardService.js, serving the dashboard's priority-action rows. It
 * moved here the moment a second surface needed the same answer
 * (useCompanyNextStep, for the "See your next step" CTA), because the rule it
 * encodes is a product decision rather than a formatting detail, and two copies
 * of it would drift the first time either was touched. The reasoning below is
 * carried over verbatim from that file.
 *
 * `/company/pipeline/:id` is a real route (CompanyNegotiationDetailPage) keyed
 * on the conversation id, the same id the board's own row click opens, so a
 * control that names a single negotiation opens that negotiation rather than
 * dropping the recruiter on a board to go find it. With more than one waiting
 * there is no single row to open, so the board is the honest destination.
 *
 * NEVER a synthetic pending-interest row. Those carry `needsListing: true` and
 * an `_id` prefixed "interest:", they have no Swipe and no negotiation behind
 * them, and CompanyNegotiationsPage.openNegotiation returns early for them. A
 * deep link to one would resolve to a NegotiationsPage that cannot find a
 * match. Callers filter them out before they get here; this guard is the second
 * line, not the first.
 */

export const PIPELINE_PATH = '/company/pipeline';

/** An id that `/company/pipeline/:id` can actually resolve. */
const openableId = (row) => {
  const id = row?.conversationId || row?._id;
  if (!id || row?.needsListing) return null;
  const text = String(id);
  // "review" is declared BEFORE ":id" in App.js on purpose; a row whose id were
  // that literal would be swallowed by the review page. Vanishingly unlikely
  // with ObjectIds, free to rule out.
  if (text.startsWith('interest:') || text === 'review') return null;
  return text;
};

/**
 * @param {Array} rows - the negotiations this control is about
 * @returns {string} the deep link when there is exactly one openable row,
 *   otherwise the board
 */
export const negotiationLink = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length !== 1) return PIPELINE_PATH;
  const id = openableId(list[0]);
  return id ? `${PIPELINE_PATH}/${encodeURIComponent(id)}` : PIPELINE_PATH;
};

export default negotiationLink;
